/**
 * Suppliers — CRM Fournisseurs intelligent (miroir strict de Clients.jsx).
 *
 *   - 4 KPI (Total, Actifs 3m, Récurrents, Volume 12m)
 *   - Chips statuts (Stratégiques, Actifs, À risque, Dormants, Nouveaux)
 *   - Toolbar : recherche + filtres source + tranche volume + toggles
 *   - Tri sur toutes les colonnes clés
 *   - Vue cards OU tableau (toggle)
 *   - Export Excel, Import Excel, Nettoyer doublons
 *   - Sheet latéral + fiche 360° dédiée /app/suppliers/:id
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, Building2, Mail, MapPin, FileText, Plus, Pencil, Trash2, Upload, Merge,
  Users, TrendingUp, Star, AlertCircle, Download, Save, RefreshCw,
  LayoutGrid, List, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import SupplierEditorSheet from "@/components/SupplierEditorSheet";
import SmartImportModal from "@/components/SmartImportModal";

const fmtEuro = (n, digits = 0) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  })} €`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR") : "—";

const SOURCE_STYLES = {
  native:       { label: "Manuel",     cls: "bg-slate-50 text-slate-700 border-slate-200" },
  manual:       { label: "Manuel",     cls: "bg-slate-50 text-slate-700 border-slate-200" },
  axonaut:      { label: "Axonaut",    cls: "bg-blue-50 text-blue-700 border-blue-200" },
  pennylane:    { label: "Pennylane",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ocr:          { label: "OCR IA",     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  gemini:       { label: "OCR IA",     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  smart_import: { label: "Import XLS", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const STATUS_META = {
  new:       { label: "🆕 Nouveau",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  strategic: { label: "🌟 Stratégique", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  active:    { label: "✅ Actif",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dormant:   { label: "💤 Dormant",     cls: "bg-slate-100 text-slate-600 border-slate-200" },
  at_risk:   { label: "⚠️ À risque",    cls: "bg-red-50 text-red-700 border-red-200" },
};

// ============================================================================
// KPI header
// ============================================================================
function KpiCards({ suppliers, totalVolume12m }) {
  const total = suppliers.length;
  const active = suppliers.filter((s) => {
    if (!s.last_invoice_date) return false;
    const d = new Date(s.last_invoice_date);
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 3);
    return d >= cutoff;
  }).length;
  const recurring = suppliers.filter((s) => s.is_recurring).length;

  const cards = [
    { label: "Total fournisseurs", value: total, icon: Users, accent: "blue" },
    { label: "Actifs (3 mois)", value: active, icon: TrendingUp, accent: "emerald" },
    { label: "Récurrents", value: recurring, icon: Star, accent: "amber" },
    { label: "Volume 12 m", value: fmtEuro(totalVolume12m), icon: Building2, accent: "slate" },
  ];
  const accents = {
    blue:    "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    slate:   "bg-slate-100 text-slate-700",
  };
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <Card key={c.label}
              className="bg-white border-slate-200 rounded-xl p-4"
              data-testid={`supplier-kpi-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", accents[c.accent])}>
              <c.icon className="h-4 w-4" strokeWidth={2} />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">
              {c.label}
            </div>
          </div>
          <div className="text-2xl font-semibold font-mono tabular-nums text-slate-900">
            {c.value}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============================================================================
// Supplier Card
// ============================================================================
function SupplierCard({ supplier, onSelect, idx }) {
  const srcKey = supplier.sources?.[0] || supplier.source || "native";
  const src = SOURCE_STYLES[srcKey] || SOURCE_STYLES.native;
  const status = STATUS_META[supplier.status] || STATUS_META.new;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.02 }} onClick={() => onSelect(supplier)}
                className="cursor-pointer" data-testid={`supplier-card-${supplier.id}`}>
      <Card className="bg-white border-slate-200 rounded-xl p-5 card-hover h-full flex flex-col hover:border-slate-300 transition-colors">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm truncate"
                style={{ fontFamily: "Manrope, sans-serif" }} title={supplier.name}>
              {supplier.name}
            </h3>
            {supplier.city && (
              <p className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                {supplier.city}{supplier.postal_code && ` · ${supplier.postal_code}`}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge variant="outline" className={cn("text-[9px] uppercase", status.cls)}>
              {status.label}
            </Badge>
            <Badge variant="outline" className={cn("text-[9px] uppercase", src.cls)}>
              {src.label}
            </Badge>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2 pb-3 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Vol. 12 m</div>
            <div className="text-sm font-semibold font-mono tabular-nums text-blue-700">
              {fmtEuro(supplier.volume_12m)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Factures</div>
            <div className="text-sm font-semibold font-mono tabular-nums text-slate-900">
              {supplier.invoice_count || 0}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-slate-500">
            Dernière : <span className="font-mono text-slate-700">{fmtDate(supplier.last_invoice_date)}</span>
          </span>
          {supplier.overdue_ttc > 0.01 ? (
            <span className="text-red-700 font-semibold font-mono">
              Retard {fmtEuro(supplier.overdue_ttc)}
            </span>
          ) : supplier.outstanding_ttc > 0.01 ? (
            <span className="text-amber-700 font-semibold font-mono">
              Reste dû {fmtEuro(supplier.outstanding_ttc)}
            </span>
          ) : (
            <span className="text-emerald-600 text-[10px]">Soldé</span>
          )}
        </div>
        {supplier.concentration_pct >= 30 && (
          <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-red-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Concentration {supplier.concentration_pct}% du volume 12 m
          </div>
        )}
      </Card>
    </motion.div>
  );
}

// ============================================================================
// Supplier Sheet (latéral)
// ============================================================================
function SupplierSheet({ supplier, open, onClose, onReload, onEdit, canWrite }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!supplier || !open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/suppliers/${supplier.id}`);
        setDetail(data);
      } catch (e) { toast.error(apiError(e)); onClose(); }
      finally { setLoading(false); }
    })();
  }, [supplier, open]);  // eslint-disable-line

  const toggleRecurring = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.patch(`/suppliers/${supplier.id}`,
        { is_recurring: !detail.supplier.is_recurring_manual });
      toast.success("Récurrence mise à jour");
      const { data } = await api.get(`/suppliers/${supplier.id}`);
      setDetail(data); onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveNotes = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.patch(`/suppliers/${supplier.id}`,
        { notes: detail.supplier.notes || "" });
      toast.success("Notes enregistrées"); onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!supplier) return;
    if (!window.confirm(`Supprimer le fournisseur "${supplier.name}" ?`)) return;
    try {
      await api.delete(`/suppliers/${supplier.id}`);
      toast.success("Fournisseur supprimé");
      onReload(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[540px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <SheetTitle className="text-lg text-slate-900 truncate"
                          style={{ fontFamily: "Manrope, sans-serif" }}>
                {supplier?.name}
              </SheetTitle>
              <SheetDescription className="text-xs text-slate-500">
                {supplier?.invoice_count || 0} facture{supplier?.invoice_count > 1 ? "s" : ""}
                {" · Vol. 12m "}{fmtEuro(supplier?.volume_12m)}
              </SheetDescription>
            </div>
            {canWrite && (
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm"
                        onClick={() => navigate(`/app/suppliers/${supplier.id}`)}
                        className="border-slate-200" data-testid="supplier-sheet-open360-btn">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Fiche 360°
                </Button>
                <Button variant="outline" size="sm" onClick={onEdit}
                         className="border-slate-200" data-testid="supplier-sheet-edit-btn">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={remove}
                         className="border-red-200 text-red-600 hover:bg-red-50"
                         data-testid="supplier-sheet-delete-btn">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </SheetHeader>
        <ScrollArea className="flex-1 px-6 py-5">
          {loading ? <div className="skeleton h-24 rounded-lg" /> : detail ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-slate-200 bg-white p-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">Vol. 12m</div>
                  <div className="text-sm font-semibold text-blue-700">{fmtEuro(detail.supplier.volume_12m)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">Panier moy.</div>
                  <div className="text-sm font-semibold">{fmtEuro(detail.supplier.avg_ticket)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">Reste dû</div>
                  <div className={cn("text-sm font-semibold",
                                     detail.supplier.overdue_ttc > 0 ? "text-red-700"
                                     : detail.supplier.outstanding_ttc > 0 ? "text-amber-700"
                                     : "text-emerald-700")}>
                    {fmtEuro(detail.supplier.outstanding_ttc)}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Coordonnées</h4>
                <div className="space-y-1 text-sm text-slate-700">
                  {detail.supplier.contact_email && (
                    <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{detail.supplier.contact_email}</div>
                  )}
                  {detail.supplier.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <span>{detail.supplier.address}{detail.supplier.postal_code && `, ${detail.supplier.postal_code}`}{detail.supplier.city && ` ${detail.supplier.city}`}</span>
                    </div>
                  )}
                  {detail.supplier.siret && (
                    <div className="flex items-center gap-2 font-mono text-xs">
                      <Building2 className="h-3.5 w-3.5 text-slate-400" />SIRET {detail.supplier.siret}
                    </div>
                  )}
                  {detail.supplier.siren && !detail.supplier.siret && (
                    <div className="font-mono text-xs text-slate-500">SIREN {detail.supplier.siren}</div>
                  )}
                  {detail.supplier.vat_number && (
                    <div className="font-mono text-xs text-slate-500">TVA {detail.supplier.vat_number}</div>
                  )}
                </div>
              </div>
              {canWrite && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="supplier-sheet-recurring" className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Fournisseur récurrent
                    </label>
                    <Switch id="supplier-sheet-recurring" checked={!!detail.supplier.is_recurring_manual}
                            onCheckedChange={toggleRecurring} disabled={saving}
                            data-testid="supplier-recurring-toggle" />
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Le flag "auto" (≥ 3 factures / 12 m) reste actif indépendamment de ce toggle.
                  </p>
                  <div className="mt-3">
                    <label htmlFor="supplier-sheet-notes" className="text-xs uppercase tracking-wider text-slate-500 font-medium">Notes</label>
                    <Textarea id="supplier-sheet-notes" value={detail.supplier.notes || ""}
                              onChange={(e) => setDetail({ ...detail, supplier: { ...detail.supplier, notes: e.target.value } })}
                              placeholder="Notes internes…" className="mt-1 text-xs"
                              rows={3} data-testid="supplier-notes" />
                    <Button onClick={saveNotes} disabled={saving} size="sm"
                            className="mt-2" data-testid="supplier-save-notes-btn">
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {saving ? "Enregistrement…" : "Enregistrer notes"}
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                  Historique ({detail.invoices.length})
                </h4>
                <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                  {detail.invoices.map((inv) => (
                    <Link key={inv.id} to={`/app/purchases/supplier-invoices/${inv.id}`}
                          className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50 text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="font-mono text-blue-700 truncate">{inv.invoice_number}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-slate-500 font-mono">{fmtDate(inv.invoice_date)}</span>
                        <span className="font-mono font-semibold">{fmtEuro(inv.total_ttc)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Main page
// ============================================================================
export default function Suppliers() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("expenses.write");
  const [suppliers, setSuppliers] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalVolume12m, setTotalVolume12m] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [onlyRecurring, setOnlyRecurring] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [volumeBucket, setVolumeBucket] = useState("all");
  const [sortBy, setSortBy] = useState("volume_total");
  const [sortDir, setSortDir] = useState("desc");
  const [view, setView] = useState("cards");
  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        only_recurring: onlyRecurring, only_active: onlyActive,
        only_overdue: onlyOverdue, sort_by: sortBy, sort_dir: sortDir,
      };
      if (statusFilter !== "all") params.status = statusFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (volumeBucket === "lt1k") params.max_volume = 1000;
      else if (volumeBucket === "1k_10k") { params.min_volume = 1000; params.max_volume = 10000; }
      else if (volumeBucket === "10k_100k") { params.min_volume = 10000; params.max_volume = 100000; }
      else if (volumeBucket === "gt100k") params.min_volume = 100000;
      const { data } = await api.get("/suppliers", { params });
      setSuppliers(data.suppliers || []);
      setStatusCounts(data.status_counts || {});
      setTotalVolume12m(data.total_volume_12m || 0);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    onlyRecurring, onlyActive, onlyOverdue, statusFilter, sourceFilter,
    volumeBucket, sortBy, sortDir,
  ]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      (s.city || "").toLowerCase().includes(q) ||
      (s.siret || "").includes(q) || (s.siren || "").includes(q) ||
      (s.email || "").toLowerCase().includes(q));
  }, [suppliers, query]);

  const openEditor = (id = null) => { setEditingId(id); setEditorOpen(true); };

  const runNormalize = async () => {
    if (!window.confirm("Nettoyer les libellés fournisseurs (trim, casse, doublons de nom pour un même SIRET) ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/suppliers/actions/normalize");
      toast.success(`${r.data.normalized} fournisseur(s) nettoyé(s) sur ${r.data.total_suppliers}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const exportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = filtered.map((s) => ({
        Nom: s.name, Ville: s.city, CP: s.postal_code, Email: s.email,
        SIRET: s.siret, SIREN: s.siren,
        Statut: STATUS_META[s.status]?.label.replace(/^[^\w]+ /, "") || s.status,
        Source: s.sources?.join(", ") || s.source,
        "Volume total": s.volume_total, "Volume 12m": s.volume_12m,
        Factures: s.invoice_count, "Panier moyen": s.avg_ticket,
        "Reste dû": s.outstanding_ttc, "En retard": s.overdue_ttc,
        "DSO (j)": s.dso_days, Concentration: s.concentration_pct,
        "Dernière facture": s.last_invoice_date,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Fournisseurs");
      XLSX.writeFile(wb, `fournisseurs_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`${rows.length} fournisseurs exportés`);
    } catch (e) { toast.error("Export échoué"); }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir(col === "name" ? "asc" : "desc"); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Fournisseurs
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Fiches fournisseurs + agrégation temps réel depuis vos factures d'achat.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={runNormalize} disabled={busy}
                    title="Nettoie les libellés incohérents (espaces, casse, doublons SIRET)."
                    data-testid="suppliers-normalize-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Nettoyer libellés
            </Button>
            <Link to="/app/suppliers/dedup">
              <Button variant="outline"
                      data-testid="suppliers-dedup-btn"
                      className="border-amber-200 text-amber-700 hover:bg-amber-50">
                <Merge className="w-4 h-4 mr-1.5" /> Fusionner doublons
              </Button>
            </Link>
            <Button onClick={exportExcel} variant="outline"
                    data-testid="suppliers-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Exporter Excel
            </Button>
            <Button onClick={() => setImportOpen(true)} variant="outline"
                    data-testid="suppliers-import-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Importer Excel
            </Button>
            <Button onClick={() => openEditor(null)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="suppliers-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau fournisseur
            </Button>
          </div>
        )}
      </div>

      <KpiCards suppliers={suppliers} totalVolume12m={totalVolume12m} />

      {/* Chips statuts */}
      <div className="flex flex-wrap gap-1.5" data-testid="supplier-status-chips">
        {[
          { key: "all",       label: "Tous",             count: suppliers.length },
          { key: "strategic", label: "🌟 Stratégiques",  count: statusCounts.strategic },
          { key: "active",    label: "✅ Actifs",         count: statusCounts.active },
          { key: "at_risk",   label: "⚠️ À risque",       count: statusCounts.at_risk },
          { key: "dormant",   label: "💤 Dormants",       count: statusCounts.dormant },
          { key: "new",       label: "🆕 Nouveaux",       count: statusCounts.new },
        ].map((s) => (
          <button key={s.key} onClick={() => setStatusFilter(s.key)}
                  data-testid={`supplier-chip-${s.key}`}
                  className={cn(
                    "text-xs px-3 py-1.5 rounded-full border transition-colors",
                    statusFilter === s.key
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            {s.label} <span className="ml-1 opacity-70 font-mono">{s.count || 0}</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input id="suppliers-search" name="suppliers-search" value={query}
                 onChange={(e) => setQuery(e.target.value)}
                 placeholder="Rechercher (nom, ville, SIRET, email…)"
                 className="pl-8 bg-white border-slate-200"
                 data-testid="suppliers-search" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select id="suppliers-source-filter" name="suppliers-source-filter"
                  value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                  className="text-xs px-2 py-2 rounded-md border border-slate-200 bg-white"
                  data-testid="supplier-source-filter">
            <option value="all">Toutes sources</option>
            <option value="native">Manuel</option>
            <option value="axonaut">Axonaut</option>
            <option value="pennylane">Pennylane</option>
            <option value="ocr">OCR IA</option>
          </select>
          <select id="suppliers-volume-filter" name="suppliers-volume-filter"
                  value={volumeBucket} onChange={(e) => setVolumeBucket(e.target.value)}
                  className="text-xs px-2 py-2 rounded-md border border-slate-200 bg-white"
                  data-testid="supplier-volume-filter">
            <option value="all">Toutes tranches volume</option>
            <option value="lt1k">&lt; 1 k€</option>
            <option value="1k_10k">1–10 k€</option>
            <option value="10k_100k">10–100 k€</option>
            <option value="gt100k">&gt; 100 k€</option>
          </select>
          <button onClick={() => setOnlyOverdue(!onlyOverdue)}
                  data-testid="filter-supplier-overdue"
                  className={cn(
                    "text-xs px-3 py-2 rounded-md border transition-colors flex items-center gap-1.5",
                    onlyOverdue ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            <AlertCircle className="h-3.5 w-3.5" />En retard
          </button>
          <button onClick={() => setOnlyActive(!onlyActive)}
                  data-testid="filter-supplier-active"
                  className={cn(
                    "text-xs px-3 py-2 rounded-md border transition-colors flex items-center gap-1.5",
                    onlyActive ? "bg-blue-50 text-blue-700 border-blue-200"
                               : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            <TrendingUp className="h-3.5 w-3.5" />Actifs (3m)
          </button>
          <button onClick={() => setOnlyRecurring(!onlyRecurring)}
                  data-testid="filter-supplier-recurring"
                  className={cn(
                    "text-xs px-3 py-2 rounded-md border transition-colors flex items-center gap-1.5",
                    onlyRecurring ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            <Star className="h-3.5 w-3.5" />Récurrents
          </button>
          <div className="ml-auto flex rounded-md border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => setView("cards")} data-testid="supplier-view-cards"
                    className={cn("px-2.5 py-1.5", view === "cards" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50")}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => setView("table")} data-testid="supplier-view-table"
                    className={cn("px-2.5 py-1.5", view === "table" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50")}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="skeleton h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-16 text-center bg-white border-slate-200 rounded-xl">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">
            {suppliers.length === 0
              ? "Aucun fournisseur encore. Importez une facture ou un Excel pour commencer."
              : "Aucun résultat pour ces filtres."}
          </p>
        </Card>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s, i) => (
            <SupplierCard key={s.id} supplier={s} onSelect={setSelected} idx={i} />
          ))}
        </div>
      ) : (
        <Card className="bg-white border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="suppliers-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
              <tr>
                <SortHead label="Fournisseur" col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortHead label="Statut" col="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortHead label="Volume 12m" col="volume_12m" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHead label="Volume total" col="volume_total" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHead label="Factures" col="invoice_count" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHead label="Reste dû" col="outstanding_ttc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHead label="DSO" col="dso_days" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHead label="Dernière" col="last_invoice_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const st = STATUS_META[s.status] || STATUS_META.new;
                return (
                  <tr key={s.id} onClick={() => setSelected(s)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`supplier-row-${s.id}`}>
                    <td className="p-3">
                      <div className="font-medium text-slate-900 truncate max-w-[240px]">{s.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {s.city || "—"}{s.siret && ` · ${s.siret}`}
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={cn("text-[10px]", st.cls)}>{st.label}</Badge>
                    </td>
                    <td className="p-3 text-right font-mono text-blue-700">{fmtEuro(s.volume_12m)}</td>
                    <td className="p-3 text-right font-mono">{fmtEuro(s.volume_total)}</td>
                    <td className="p-3 text-right font-mono">{s.invoice_count}</td>
                    <td className={cn("p-3 text-right font-mono",
                                     s.overdue_ttc > 0 ? "text-red-700"
                                     : s.outstanding_ttc > 0 ? "text-amber-700" : "text-slate-400")}>
                      {s.outstanding_ttc > 0.01 ? fmtEuro(s.outstanding_ttc) : "—"}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {s.dso_days === null || s.dso_days === undefined
                        ? <span className="text-slate-400">—</span>
                        : `${s.dso_days} j`}
                    </td>
                    <td className="p-3 text-xs text-slate-600 font-mono">{fmtDate(s.last_invoice_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <SupplierSheet supplier={selected} open={!!selected}
                     onClose={() => setSelected(null)} onReload={load}
                     canWrite={canWrite}
                     onEdit={canWrite ? () => { openEditor(selected?.id); setSelected(null); } : null} />

      <SupplierEditorSheet supplierId={editingId} open={editorOpen}
                            onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditingId(null); }}
                            onSaved={() => load()} />

      <SmartImportModal isOpen={importOpen} onClose={() => setImportOpen(false)}
                          onSuccess={() => load()} kind="suppliers" />
    </div>
  );
}

function SortHead({ label, col, sortBy, sortDir, onSort, align = "left" }) {
  const active = sortBy === col;
  return (
    <th className={cn("p-3 text-xs cursor-pointer select-none",
                      align === "right" && "text-right")}
        onClick={() => onSort(col)}>
      <span className={cn("inline-flex items-center gap-1",
                          align === "right" && "flex-row-reverse")}>
        {label}
        {active && (sortDir === "asc" ? "↑" : "↓")}
      </span>
    </th>
  );
}
